import React, { useMemo } from 'react'
import { useAnalyticsMetric } from '../hooks/useAnalyticsMetric'
import { Area, AreaChart } from 'recharts'
import { ChartContainer, type ChartConfig } from '@bpinternal/ui-kit-next'

type Props = {
  botId: string
  workspaceId: string
  color?: 1 | 2 | 3 | 4 | 5
  size?: 'default' | 'compact'
}

export const ErrorsAnalyticsWidget = ({ botId, workspaceId, color = 2, size = 'default' }: Props) => {
  const analyticsData = useAnalyticsMetric(botId, workspaceId, {
    extractValue: (record) => {
      if (record && typeof record === 'object' && 'llm' in record) {
        const llm = record.llm as Record<string, unknown>
        return typeof llm?.errors === 'number' ? llm.errors : 0
      }
      return 0
    },
    chartDataKey: 'errors',
    valueKey: 'last7DaysErrors',
  })

  const { last7DaysErrors, deltaFormatted, chartData } = analyticsData
  const isCompact = size === 'compact'

  const allDataZero = useMemo(() => {
    return chartData.every((item) => item.errors === 0 || item.errors === undefined)
  }, [chartData])

  const allLastWeekDataZero = useMemo(() => {
    return chartData.every((item) => item.errorsLastWeek === 0 || item.errorsLastWeek === undefined)
  }, [chartData])

  const chartConfig: ChartConfig = {
    errors: {
      label: 'Errors',
      color: allDataZero ? 'hsl(var(--chart-gray))' : `hsl(var(--chart-${color}))`,
    },
    errorsLastWeek: {
      color: allLastWeekDataZero ? 'hsl(var(--chart-gray))' : `hsl(var(--chart-${color}))`,
    },
  }

  return (
    <div className={`${isCompact ? 'h-12' : 'h-24 sm:h-20'} relative w-full min-w-0`}>
      {!isCompact && (
        <div className="truncate text-xs font-medium">
          {last7DaysErrors.toLocaleString()}
          {' Errors'}
        </div>
      )}
      <div className="truncate text-xs text-muted-foreground">{deltaFormatted}</div>
      <ChartContainer config={chartConfig} className={`${isCompact ? 'h-12' : 'h-20'} w-full min-w-0`}>
        <AreaChart accessibilityLayer data={chartData} margin={{ top: 16, bottom: 16 }}>
          <defs>
            <linearGradient id={`fillErrors-${botId}`} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={allDataZero ? 'hsl(var(--chart-gray))' : 'var(--color-errors)'}
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor={allDataZero ? 'hsl(var(--chart-gray))' : 'var(--color-errors)'}
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <Area
            dataKey="errors"
            type="bump"
            strokeWidth={1.5}
            fill={`url(#fillErrors-${botId})`}
            fillOpacity={0.4}
            stroke={allDataZero ? 'hsl(var(--chart-gray))' : 'var(--color-errors)'}
            stackId="a"
          />
          <Area
            dataKey="errorsLastWeek"
            type="bump"
            strokeWidth={1.5}
            strokeDasharray="3 3"
            fill="none"
            fillOpacity={0}
            stroke={allLastWeekDataZero ? 'hsl(var(--chart-gray))' : 'var(--color-errorsLastWeek)'}
            strokeOpacity={0.6}
            stackId="b"
          />
        </AreaChart>
      </ChartContainer>
    </div>
  )
}
